import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Calendar, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserStore } from "@/store/userStore";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { AthleteHeroBackground } from "@/components/athlete/AthleteHeroBackground";

interface CoachHeroSectionProps {
  todaySessionsCount: number;
  dayLabel: string;
}

export function CoachHeroSection({ todaySessionsCount, dayLabel }: CoachHeroSectionProps) {
  const { t } = useTranslation(["coach"]);
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { profile } = useUserProfile();
  const { athleteBackgrounds } = useThemeCustomization();

  const hours = new Date().getHours();
  let greetingKey = "hero.greeting.morning";
  if (hours >= 12 && hours < 18) greetingKey = "hero.greeting.afternoon";
  else if (hours >= 18) greetingKey = "hero.greeting.evening";

  const firstName =
    profile?.first_name || user?.email?.split("@")[0] || "Coach";

  const hasToday = todaySessionsCount > 0;

  return (
    <section className="coach-hero relative overflow-hidden min-h-[248px] md:min-h-[280px]">
      <AthleteHeroBackground src={athleteBackgrounds.dashboard} />
      <div className="coach-hero-overlay absolute inset-0" />

      <div className="relative z-10 flex min-h-[248px] flex-col justify-end p-5 md:min-h-[280px] md:p-8">
        <p className="mb-2 text-xs font-semibold uppercase text-primary md:text-sm">{dayLabel}</p>

        <h1 className="mb-2 text-3xl font-extrabold text-foreground md:text-4xl">
          {t(greetingKey)} {firstName} 👋
        </h1>

        <p className="mb-5 max-w-md text-sm text-foreground/75 md:text-base">
          {hasToday
            ? `${t("hero.subtitleToday")} · ${todaySessionsCount}`
            : t("hero.subtitleEmpty")}
        </p>

        <Button
          size="lg"
          onClick={() => navigate("/coach/schedule")}
          className="coach-primary-cta group w-full transition-all sm:w-fit sm:min-w-80"
        >
          <Calendar className="mr-2 h-5 w-5" />
          {t("hero.ctaSchedule")}
          <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </Button>
      </div>
    </section>
  );
}
